// Departure Time Calculator Service
// Calculates when to leave for the airport based on home address, flight time, and traveler preferences
// Now includes real-time traffic integration

import { getAirportTravelTime } from './trafficService';

// Security wait time estimates (in minutes)
const SECURITY_WAIT_TIMES = {
  standard: 30,      // Regular TSA line
  precheck: 10,      // TSA PreCheck
  clear: 5,          // Clear + TSA PreCheck
  clearOnly: 20      // Clear but no PreCheck
};

// Additional buffer times (in minutes)
const BUFFER_TIMES = {
  checkIn: 15,       // Check-in and bag drop
  gate: 10,          // Walk to gate
  boarding: 15       // Boarding starts 15 min before departure
};

/**
 * Calculate departure time for a flight
 * @param {Object} flight - Flight object with departure info
 * @param {Object} userProfile - User profile with address and traveler info
 * @param {boolean} isDepartureFlight - True if this is the first/departure flight from home
 * @returns {Object} Departure calculation result
 */
export const calculateDepartureTime = async (flight, userProfile, isDepartureFlight = true, isConnectingFlight = false, previousFlight = null) => {
  try {

    if (!flight || !userProfile) {
      throw new Error('Flight and user profile are required');
    }

    // Parse flight departure time
    const departureTime = new Date(flight.departureTime);
    if (isNaN(departureTime.getTime())) {
      throw new Error('Invalid flight departure time');
    }

    // Calculate security wait time based on user's TSA PreCheck/Clear status
    const securityWaitTime = calculateSecurityWaitTime(userProfile.travelerInfo);

    // Calculate travel time based on flight type
    let travelData;
    
    if (isDepartureFlight) {
      // First flight: travel from home to airport
      travelData = await getAirportTravelTime(
        userProfile.homeAddress, 
        flight.departureAirport, 
        flight.departureTime
      );
    } else if (isConnectingFlight && previousFlight) {
      // Connecting flight: travel from arrival gate to departure gate (estimated)
      const layoverTimeMinutes = Math.floor((new Date(flight.departureTime) - new Date(previousFlight.arrivalTime)) / (1000 * 60));
      
      // For connecting flights, we estimate travel time within the airport
      // This includes time to get from arrival gate to departure gate
      const airportTravelTime = 15; // 15 minutes average gate-to-gate time
      
      travelData = {
        travelTimeMinutes: airportTravelTime,
        hasTrafficData: false,
        trafficDelayMinutes: 0,
        layoverTimeMinutes: layoverTimeMinutes,
        isConnectingFlight: true
      };
    } else {
      // Return flight: use estimates since we don't know the origin location
      travelData = await getAirportTravelTime(
        null,  // No origin for return flight - will use fallback
        flight.departureAirport, 
        flight.departureTime
      );
    }
    
    const travelTime = travelData.travelTimeMinutes;
    const hasTrafficData = travelData.hasTrafficData;
    const trafficDelay = travelData.trafficDelayMinutes;

    // Calculate total time needed before departure
    // For connecting flights, we don't need security wait time again
    const totalTimeNeeded = isConnectingFlight
      ? travelTime + BUFFER_TIMES.gate + BUFFER_TIMES.boarding
      : travelTime + securityWaitTime + BUFFER_TIMES.checkIn + BUFFER_TIMES.gate + BUFFER_TIMES.boarding;

    // Add 2-hour buffer before flight departure
    const twoHourBuffer = 2 * 60; // 2 hours in minutes
    const totalTimeWithBuffer = totalTimeNeeded + twoHourBuffer;
    

    // Calculate departure time (subtract total time + buffer from flight departure)
    const departureTimeMs = departureTime.getTime() - (totalTimeWithBuffer * 60 * 1000);
    const suggestedDepartureTime = new Date(departureTimeMs);

    const result = {
      suggestedDepartureTime,
      travelTime,
      securityWaitTime,
      totalTimeNeeded,
      totalTimeWithBuffer,
      twoHourBuffer,
      isConnectingFlight,
      layoverTimeMinutes: travelData.layoverTimeMinutes || 0,
      trafficData: {
        hasTrafficData,
        trafficDelay,
        distanceMiles: travelData.distanceMiles,
        route: travelData.route,
        timestamp: travelData.timestamp,
        fallback: travelData.fallback || false
      },
      breakdown: {
        travelTime,
        securityWaitTime,
        checkIn: BUFFER_TIMES.checkIn,
        gate: BUFFER_TIMES.gate,
        boarding: BUFFER_TIMES.boarding,
        twoHourBuffer
      },
      userPreferences: {
        hasPreCheck: userProfile.travelerInfo?.tsaPreCheck || false,
        hasClear: userProfile.travelerInfo?.clear || false,
        hasGlobalEntry: userProfile.travelerInfo?.globalEntry || false
      }
    };

    return result;

  } catch (error) {
    console.error('Error calculating departure time:', error);
    return {
      error: error.message,
      suggestedDepartureTime: null,
      travelTime: null,
      securityWaitTime: null,
      totalTimeNeeded: null
    };
  }
};

/**
 * Calculate security wait time based on user's TSA PreCheck/Clear status
 * @param {Object} travelerInfo - User's traveler information
 * @returns {number} Wait time in minutes
 */
const calculateSecurityWaitTime = (travelerInfo) => {
  if (!travelerInfo) {
    return SECURITY_WAIT_TIMES.standard;
  }

  const hasPreCheck = travelerInfo.tsaPreCheck || false;
  const hasClear = travelerInfo.clear || false;

  if (hasClear && hasPreCheck) {
    return SECURITY_WAIT_TIMES.clear; // Best case: Clear + PreCheck
  } else if (hasClear && !hasPreCheck) {
    return SECURITY_WAIT_TIMES.clearOnly; // Clear but no PreCheck
  } else if (hasPreCheck && !hasClear) {
    return SECURITY_WAIT_TIMES.precheck; // PreCheck only
  } else {
    return SECURITY_WAIT_TIMES.standard; // Regular TSA line
  }
};

/**
 * Format time for display
 * @param {Date} date - Date object
 * @returns {string} Formatted time string
 */
export const formatDepartureTime = (date) => {
  if (!date || isNaN(date.getTime())) {
    return 'Unable to calculate';
  }

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format duration for display
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration string
 */
export const formatDuration = (minutes) => {
  if (!minutes || minutes < 0) {
    return 'Unknown';
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  } else {
    return `${mins}m`;
  }
};

/**
 * Calculate departure time from any address to airport
 * @param {string} fromAddress - Starting address (e.g., meeting location)
 * @param {Object} flight - Flight object with departure info
 * @param {Object} userProfile - User profile with traveler preferences
 * @returns {Object} Departure calculation result
 */
export const calculateDepartureFromAddress = async (fromAddress, flight, userProfile) => {
  try {
    
    if (!fromAddress || !flight || !userProfile) {
      throw new Error('Missing required parameters for departure calculation');
    }

    const flightDepartureTime = new Date(flight.departureTime);
    const departureAirport = flight.departureAirport;

    
    // Get travel time from the specified address to airport
    const trafficData = await getAirportTravelTime(fromAddress, departureAirport, flight.departureTime);
    
    
    if (!trafficData || trafficData.travelTimeMinutes === null) {
      throw new Error('Unable to calculate travel time from address to airport');
    }

    // Calculate security wait time based on user preferences
    let securityWaitTime = SECURITY_WAIT_TIMES.standard;
    const travelerInfo = userProfile.travelerInfo || {};
    
    if (travelerInfo.clear && travelerInfo.tsaPreCheck) {
      securityWaitTime = SECURITY_WAIT_TIMES.clear;
    } else if (travelerInfo.tsaPreCheck) {
      securityWaitTime = SECURITY_WAIT_TIMES.precheck;
    } else if (travelerInfo.clear) {
      securityWaitTime = SECURITY_WAIT_TIMES.clearOnly;
    }

    // Calculate total buffer time
    const totalBufferTime = BUFFER_TIMES.checkIn + BUFFER_TIMES.gate + BUFFER_TIMES.boarding;
    
    // Add 2-hour buffer before flight departure (same as regular calculation)
    const twoHourBuffer = 2 * 60; // 2 hours in minutes
    
    // Calculate total time needed before flight departure
    const totalTimeNeeded = trafficData.travelTimeMinutes + securityWaitTime + totalBufferTime;
    const totalTimeWithBuffer = totalTimeNeeded + twoHourBuffer;
    
    
    // Calculate suggested departure time (subtract total time + buffer from flight departure)
    const suggestedDepartureTime = new Date(flightDepartureTime.getTime() - (totalTimeWithBuffer * 60 * 1000));

    const result = {
      success: true,
      fromAddress,
      toAirport: departureAirport,
      flightDepartureTime: flightDepartureTime.toISOString(),
      suggestedDepartureTime: suggestedDepartureTime.toISOString(),
      travelTimeMinutes: trafficData.travelTimeMinutes,
      securityWaitTime,
      totalBufferTime,
      twoHourBuffer,
      totalTimeNeeded,
      totalTimeWithBuffer,
      trafficData,
      userPreferences: {
        hasPreCheck: travelerInfo.tsaPreCheck || false,
        hasClear: travelerInfo.clear || false,
        hasGlobalEntry: travelerInfo.globalEntry || false
      },
      calculatedAt: new Date().toISOString()
    };

    return result;

  } catch (error) {
    console.error('Error calculating departure from address:', error);
    return {
      success: false,
      error: error.message,
      fromAddress,
      calculatedAt: new Date().toISOString()
    };
  }
};
